import { Injectable } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Apollo } from 'apollo-angular';
import { ulid } from 'ulid';
import { BehaviorSubject, Observable, switchMap, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { cleanTypename } from '@modules/segments/helpers/handleData.helper';
import {
  GET_WORKFLOWS,
  GET_WORKFLOW_TRIGGERS,
  GET_LINKED_WORKFLOWS,
  GET_WORKFLOW_PRIORITY_BY_TENANT,
  CREATE_WORKFLOW,
  UPDATE_WORKFLOW,
  PUBLISH_WORKFLOW,
  PAUSE_WORKFLOW,
  UNPUBLISH_WORKFLOW,
  DELETE_WORKFLOW,
  UPDATE_WORKFLOW_PRIORITY,
} from '@modules/campaigns-new/Infrastructure/GraphQL/workflow.graphql';
import { WorkflowType } from '@modules/campaigns-new/Domain/Enums/WorkflowType';
import { IWorkflow } from '@modules/campaigns-new/Domain/Interfaces/IWorkflow';

/**
 * WorkflowService — pure infrastructure adapter for the Workflow aggregate.
 *
 * Responsibilities: GraphQL calls only. No business logic, no UI state.
 * All methods return observables or promises — callers decide how to react.
 */
@Injectable({ providedIn: 'root' })
export class WorkflowService {
  readonly isLoading$ = new BehaviorSubject<boolean>(false);

  constructor(
    private route: ActivatedRoute,
    private apollo: Apollo,
  ) {}

  private get tenantId(): string {
    return this.route.snapshot.queryParams['tenant'];
  }

  // ─── Queries ────────────────────────────────────────────────────────────────

  getWorkflows(type: WorkflowType | WorkflowType[]): Observable<IWorkflow[]> {
    const types = Array.isArray(type) ? type : [type];
    return this.apollo
      .query({
        query: GET_WORKFLOWS,
        variables: { tenantId: this.tenantId, types },
        fetchPolicy: 'no-cache',
      })
      .pipe(
        map((res: any) => {
          if (res.errors?.length) return [];
          return res.data.workflows as IWorkflow[];
        }),
      );
  }

  getTriggers(): Observable<any[]> {
    return this.apollo
      .query({
        query: GET_WORKFLOW_TRIGGERS,
        variables: { tenantId: this.tenantId },
      })
      .pipe(map((res: any) => res.data?.workflowTriggers ?? []));
  }

  getLinkedWorkflows(id: string): Observable<any[]> {
    return this.apollo
      .query({ query: GET_LINKED_WORKFLOWS, variables: { id } })
      .pipe(
        map((res: any) => {
          if (res?.errors?.length) throw new Error(res.errors[0].message);
          return res.data.linkedWorkflows;
        }),
        catchError((err) => throwError(() => new Error(err))),
      );
  }

  getWorkflowPriority(): Promise<string[]> {
    return new Promise((resolve, reject) => {
      this.apollo
        .query({
          query: GET_WORKFLOW_PRIORITY_BY_TENANT,
          variables: { tenantId: this.tenantId },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) reject(res.errors[0].message);
          else resolve(res.data.workflowPriorityByTenant.workflowIds);
        });
    });
  }

  // ─── Mutations ──────────────────────────────────────────────────────────────

  publishWorkflow(workflow: { id: string }): Observable<any> {
    return this.apollo
      .mutate({ mutation: PUBLISH_WORKFLOW, variables: { input: { id: workflow.id } } })
      .pipe(map((res: any) => res));
  }

  pauseWorkflow(workflow: { id: string }): Observable<any> {
    return this.apollo
      .mutate({ mutation: PAUSE_WORKFLOW, variables: { input: { id: workflow.id } } })
      .pipe(map((res: any) => res));
  }

  unpublishWorkflow(workflow: { id: string }): Observable<any> {
    return this.apollo
      .mutate({ mutation: UNPUBLISH_WORKFLOW, variables: { input: { id: workflow.id } } })
      .pipe(map((res: any) => res));
  }

  deleteWorkflow(workflow: { id: string }): Observable<any> {
    return this.apollo
      .mutate({ mutation: DELETE_WORKFLOW, variables: { id: workflow.id } })
      .pipe(map((res: any) => res));
  }

  updateWorkflowPriority(priority: any): Promise<void> {
    delete priority['__typename'];
    return new Promise((resolve, reject) => {
      this.apollo
        .mutate({
          mutation: UPDATE_WORKFLOW_PRIORITY,
          variables: {
            input: { tenantId: this.tenantId, workflowIds: priority },
          },
        })
        .subscribe((res: any) => {
          if (res.errors?.length) reject(res.errors[0].message);
          else resolve(res.data.updateWorkflow);
        });
    });
  }

  createWorkflow(input: Record<string, any>): Observable<any> {
    return this.apollo
      .mutate({ mutation: CREATE_WORKFLOW, variables: { input } })
      .pipe(map((res: any) => res.data.createWorkflow));
  }

  updateWorkflow(input: Record<string, any>): Observable<any> {
    return this.apollo
      .mutate({ mutation: UPDATE_WORKFLOW, variables: { input } })
      .pipe(map((res: any) => res));
  }
}
